import { create } from 'zustand';
import type { Contact } from '@/types/contact';

export type JoinableMeeting = {
  id: string;
  wherebyRoomUrl: string;
  startTime: string;
  endTime: string;
}

export interface ContactsState {
  /* data */
  mentors:            Contact[] | null;
  mentees:            Contact[] | null;
  currentContact:     Contact | null;
  meeting:            JoinableMeeting | null;
  contactsLoading:    boolean;
  joinedMeeting:      boolean;
  calendarIsOpen:     boolean;
  error:              string | null;

  /* actions */
  fetchContacts:       () => Promise<void>;
  refreshContacts:     () => Promise<void>;
  setMentees:          (contacts: Contact[]) => void;
  setMentors:          (contacts: Contact[]) => void;
  setCurrentContact:   (c: Contact | null) => void;
  getCurrentContact:   () => Contact | null;
  joinMeeting:         (m: JoinableMeeting | null) => void;
  leaveMeeting:        () => void;
  toggleCalendar:      () => void;
  setCalendarOpen:     (isOpen: boolean) => void;
  reset:               () => void;
}

const initialState = {
  mentors:        null,
  mentees:        null,
  currentContact:  null,
  meeting:         null,
  contactsLoading: false,
  joinedMeeting:   false,
  calendarIsOpen:  false,
  error:           null,
};

export const useContactsStore = create<ContactsState>((set, get) => ({
  /* ------------- state ------------- */
  ...initialState,

  /* ------------- actions ----------- */
  fetchContacts: async () => {
    /* short-circuit if we already have data */
    if (get().mentors && get().mentees) return;

    set({ contactsLoading: true, error: null });
    try {
      const res  = await fetch(`/api/contacts`);
      const data = await res.json();
      set({
        mentors: data?.mentors ?? [],
        mentees: data?.mentees ?? [],
        contactsLoading: false
      });
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      set({ error: 'Failed to load contacts', contactsLoading: false });
    }
  },

  refreshContacts: async () => {
    /* Always fetch - no short-circuit. Used for polling. */
    try {
      const res  = await fetch(`/api/contacts`);
      const data = await res.json();
      set({
        mentors: data?.mentors ?? [],
        mentees: data?.mentees ?? [],
      });
    } catch (err) {
      console.error('Failed to refresh contacts:', err);
    }
  },

  setMentors: (mentors) => set({ mentors }),
  setMentees: (mentees) => set({ mentees }),

  setCurrentContact: (contact) =>
    set({
      currentContact: contact,
      meeting:        null,
      joinedMeeting:  false,
      calendarIsOpen: false,
      error:          null,
    }),

  getCurrentContact: () => get().currentContact,

  leaveMeeting: async () => {
    return set({ joinedMeeting: false });
  },
  joinMeeting: async (meeting) => {
    if (!meeting) return;

    try {
      /* Always call join endpoint to record attendance, even if room URL exists */
      const res = await fetch(`/api/meetings/${meeting.id}/join`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to join room');
      }

      const data = await res.json();
      if (!data.wherebyRoomUrl) {
        throw new Error('Invalid room URL received');
      }
      
      const updatedMeeting = { ...meeting, wherebyRoomUrl: data.wherebyRoomUrl };

      set({ meeting: updatedMeeting, joinedMeeting: true, error: null });
    } catch (error) {
      console.error('Failed to join meeting:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to join meeting',
        joinedMeeting: false 
      });
    }
  },

  toggleCalendar: () =>
    set((s) => ({ calendarIsOpen: !s.calendarIsOpen })),

  setCalendarOpen: (isOpen) => set({ calendarIsOpen: isOpen }),

  reset: () => set(initialState),
  resetEphemeral: () => set({
    currentContact: null,
    meeting: null,
    joinedMeeting: false,
    calendarIsOpen: false,
    error: null,
  }),
}));
